// File: src/pages/ticket/Ticket.tsx

import {
  Autocomplete,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField
} from "@mui/material";
import { useEffect, useState } from "react";
import { FiEdit, FiEye, FiFlag, FiHelpCircle, FiTrash2 } from "react-icons/fi";
import { IoTicketOutline } from "react-icons/io5";
import { TicketInterface } from "../../common/interfaces";
import { extractErrorMessage } from "../../common/utils/axios.isAxiosError";
import ConfirmDialog from "../../components/ConfirmDialog";
import CustomTooltip from "../../components/CustomTooltip";
import { PriorityLevel, PriorityLevelLabel } from "../../components/enum/priorityEnum";
import {
  TypeTicketStatus,
  TypeTicketStatusLabel
} from "../../components/enum/typeTicketStatusEnum";
import TicketCreateModal from "../../components/modal/TicketCreateModal";
import TicketDetailModal from "../../components/modal/TicketDetailModal";
import TicketStatusModal from "../../components/modal/TicketStatusModal";
import PaginationControls from "../../components/PaginationControls";
import { ToastError, ToastSuccess } from "../../components/toaster/toast.utils";
import { useAuth } from "../../hooks/useAuth";
import api from "../../services/api";
import { TextFeldUserSearch } from "../users/Users.styles";
import {
  ActionButton,
  Container,
  CreateTicketButton,
  HelpBanner,
  ProductSearchCard,
  StatusCell,
  Table,
  TableContainer,
  Title
} from "./ticket.styled";

export const Ticket = () => {
  const [tickets, setTickets] = useState<TicketInterface[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState("");

  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");

  const [ticketStatus, setTicketStatus] = useState<TypeTicketStatus>(
    TypeTicketStatus.NONE
  );

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState<TicketInterface | null>(
    null
  );

  const [companies, setCompanies] = useState<any[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<any>(null);
  const [loadingCompanies, setLoadingCompanies] = useState(false);

  const [ticketToDelete, setTicketToDelete] = useState<TicketInterface | null>(
    null
  );
  const [ticketForStatus, setTicketForStatus] =
    useState<TicketInterface | null>(null);
  const [ticketToView, setTicketToView] = useState<TicketInterface | null>(
    null
  );

  const { isAdmin, user_id } = useAuth();

  const fetchTickets = async (currentPage: number) => {
    try {
      const response = await api.get("/ticket", {
        params: {
          page: currentPage,
          limit: 8,
          search: search.trim() !== "" ? search.trim() : undefined,
          ticket_status:
            ticketStatus !== TypeTicketStatus.NONE ? ticketStatus : undefined,
          initial_date: startDate || undefined,
          final_date: endDate || undefined,
          company_id:
            isAdmin && selectedCompany ? selectedCompany.company_id : undefined
        }
      });

      setTickets(response.data.items);
      setTotalPages(response.data.meta.totalPages);
    } catch (error) {
      const msg = extractErrorMessage(error);
      console.error("Erro ao buscar tickets:", error);
      ToastError(msg);
    }
  };

  useEffect(() => {
    fetchTickets(page);
  }, [page, search, startDate, endDate, ticketStatus, selectedCompany]);

  // Lista de empresas pro filtro — só o admin enxerga esse seletor.
  useEffect(() => {
    if (!isAdmin) return;

    setLoadingCompanies(true);
    api
      .get("/company/admin")
      .then((r) => setCompanies(r.data.items || r.data))
      .catch(console.error)
      .finally(() => setLoadingCompanies(false));
  }, [isAdmin]);

  const handleOpenCreate = () => {
    setSelectedTicket(null); // Modo criação
    setModalOpen(true);
  };

  const handleOpenEdit = (ticket: TicketInterface) => {
    setSelectedTicket(ticket); // Modo edição
    setModalOpen(true);
  };

  /// Exclusão: quem abriu o ticket pode apagar o próprio; admin apaga
  /// qualquer um. O backend valida a posse de novo, isto é só a UI.
  const handleConfirmDelete = async () => {
    if (!ticketToDelete?.ticket_id) return;
    try {
      await api.delete(`/ticket/${ticketToDelete.ticket_id}`);
      ToastSuccess("Ticket excluído com sucesso!");
      fetchTickets(page);
    } catch (error) {
      const msg = extractErrorMessage(error);
      console.error("Erro ao excluir ticket:", error);
      ToastError(msg);
    } finally {
      setTicketToDelete(null);
    }
  };

  const handleTicketSaved = () => {
    fetchTickets(page);
  };

  return (
    <Container>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          marginBottom: "20px"
        }}
      >
        <Title style={{ fontSize: "34px", marginBottom: "16px" }}>Ajuda</Title>
        <CreateTicketButton onClick={handleOpenCreate}>
          <IoTicketOutline size={18} />
          Criar Ticket
        </CreateTicketButton>
      </div>

      <HelpBanner>
        <FiHelpCircle size={26} />
        <div className="text">
          <strong>Central de Ajuda</strong>
          <p>
            Esta é a área de suporte do sistema. Encontrou um problema, um bug
            ou tem alguma dúvida? Abra um ticket contando o que aconteceu e
            sinalize a prioridade — o administrador vai analisar e te ajudar
            a resolver o quanto antes.
          </p>
        </div>
      </HelpBanner>

      {isAdmin && (
        <ProductSearchCard>
          <TextField
            label="Data de Início"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ max: new Date().toLocaleDateString("en-CA") }}
          />

          <TextField
            disabled={!startDate}
            label="Data Final"
            type="date"
            value={startDate ? endDate : ""}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{
              min: startDate || undefined,
              max: new Date().toLocaleDateString("en-CA")
            }}
          />

          <TextFeldUserSearch
            id="ticket_search"
            label="Buscar"
            variant="outlined"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />

          <FormControl style={{ minWidth: 200 }}>
            <InputLabel id="label-status">Status</InputLabel>
            <Select
              labelId="label-status"
              value={ticketStatus}
              label="Status"
              onChange={(e) =>
                setTicketStatus(e.target.value as TypeTicketStatus)
              }
            >
              {(Object.values(TypeTicketStatus) as TypeTicketStatus[])
                .filter((s) => s !== TypeTicketStatus.NONE)
                .map((status) => (
                  <MenuItem key={status} value={status}>
                    {TypeTicketStatusLabel[status]}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>

          <Autocomplete
            sx={{ width: 300 }}
            options={companies}
            getOptionLabel={(opt) => opt.company_name || ""}
            value={selectedCompany}
            isOptionEqualToValue={(option, value) =>
              option?.company_id === value?.company_id
            }
            onChange={(_, v) => {
              setSelectedCompany(v);
              setPage(1);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Empresa"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingCompanies && (
                        <CircularProgress color="inherit" size={20} />
                      )}
                      {params.InputProps.endAdornment}
                    </>
                  )
                }}
              />
            )}
          />
        </ProductSearchCard>
      )}

      <TableContainer>
        <Table>
          <thead>
            <tr>
              <th>Data inicial</th>
              {isAdmin && <th>Data final</th>}
              {isAdmin && <th>Usuário</th>}
              <th>Título</th>
              <th>Status</th>
              <th>Prioridade</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {tickets.map((item) => {
              const dateObj = new Date(item.created_at || "");
              const formattedDate = dateObj.toLocaleDateString("pt-BR", {
                day: "2-digit",
                month: "2-digit",
                year: "2-digit"
              });
              const formattedTime = dateObj.toLocaleTimeString("pt-BR", {
                hour: "2-digit",
                minute: "2-digit"
              });

              const have_end_date =
                item.end_date !== null && item.end_date !== "";

              let dateEndObj = null;
              let formattedDateEnd = null;
              let formattedTimeEnd = null;
              let result = null;

              if (have_end_date) {
                dateEndObj = new Date(item.end_date || "");
                formattedDateEnd = dateEndObj.toLocaleDateString("pt-BR", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "2-digit"
                });
                formattedTimeEnd = dateEndObj.toLocaleTimeString("pt-BR", {
                  hour: "2-digit",
                  minute: "2-digit"
                });

                result = `${formattedDateEnd} ${formattedTimeEnd}`;
              } else {
                result = "Sem data de encerramento";
              }

              return (
                <tr key={item.ticket_id}>
                  <td>{`${formattedDate} ${formattedTime}`}</td>
                  {isAdmin && <td>{result}</td>}
                  {isAdmin && <td>{item.user?.user_name}</td>}
                  <td>{item.ticket_title}</td>
                  <StatusCell status={item.ticket_status!}>
                    {
                      TypeTicketStatusLabel[
                        item.ticket_status ?? TypeTicketStatus.NONE
                      ]
                    }
                  </StatusCell>
                  <td>
                    {PriorityLevelLabel[
                      (item.ticket_priority as PriorityLevel) ??
                        PriorityLevel.NONE
                    ] || "Nenhuma"}
                  </td>
                  <td>
                    <CustomTooltip title="Visualizar Ticket">
                      <ActionButton onClick={() => setTicketToView(item)}>
                        <FiEye size={20} color="#2b7bff" />
                      </ActionButton>
                    </CustomTooltip>

                    <CustomTooltip title="Editar Ticket">
                      <ActionButton onClick={() => handleOpenEdit(item)}>
                        <FiEdit size={20} color="#004336" />
                      </ActionButton>
                    </CustomTooltip>

                    {isAdmin && (
                      <CustomTooltip title="Status">
                        <ActionButton onClick={() => setTicketForStatus(item)}>
                          <FiFlag size={20} color="#1E90FF" />
                        </ActionButton>
                      </CustomTooltip>
                    )}

                    {(isAdmin || item.user_id === Number(user_id)) && (
                      <CustomTooltip title="Excluir Ticket">
                        <ActionButton onClick={() => setTicketToDelete(item)}>
                          <FiTrash2 size={20} color="#B22222" />
                        </ActionButton>
                      </CustomTooltip>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </TableContainer>

      <PaginationControls
        page={page}
        totalPages={totalPages}
        setPage={setPage}
      />

      <TicketCreateModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onTicketSaved={handleTicketSaved}
        ticket={selectedTicket}
      />

      <ConfirmDialog
        open={!!ticketToDelete}
        title="Excluir Ticket"
        description={`O ticket "${ticketToDelete?.ticket_title ?? ""}" será apagado de vez. Deseja continuar?`}
        confirmText="Excluir"
        onClose={() => setTicketToDelete(null)}
        onConfirm={handleConfirmDelete}
      />

      <TicketStatusModal
        open={!!ticketForStatus}
        onClose={() => setTicketForStatus(null)}
        onChanged={() => fetchTickets(page)}
        ticket={ticketForStatus}
      />

      <TicketDetailModal
        open={!!ticketToView}
        onClose={() => setTicketToView(null)}
        ticket={ticketToView}
      />
    </Container>
  );
};
